import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

import { ForeignNationalsAPI } from "@/api/foreignNationals";

import { useClientProfile } from "../context/ClientProfileContext";
import {
  computeCoverage,
  coverageTarget,
  EXPLAINED_GAP,
  isBeforeWindow,
  isOpenCaseStatus,
  type Coverage,
  type Gap,
  type HistoryKind,
  type Period
} from "../lib/coverage";

export type CoverageItem<R> =
  | { type: "row"; row: R; faded: boolean }
  | { type: "gap"; gap: Gap };

/**
 * Coverage of the client's address or employment history against what the
 * open cases' forms ask for, and the rows interleaved with the gaps (newest
 * first). Employment counts study periods and explained breaks as covered.
 */
export const useHistoryCoverage = <R>(
  kind: HistoryKind,
  rows: R[],
  periodOf: (row: R) => Period & { label?: string },
  ready: boolean
): { coverage: Coverage | null; items: CoverageItem<R>[] } => {
  const { clientId, client, matters } = useClientProfile();
  const [study, setStudy] = useState<Period[]>([]);

  useEffect(() => {
    if (kind !== "employment") return undefined;
    let live = true;
    ForeignNationalsAPI.listEducationHistoryEntries(clientId)
      .then((list) => {
        if (!live) return;
        setStudy(
          list.map((e) => ({
            start: e.start_date,
            end: e.end_date,
            current: e.is_current
          }))
        );
      })
      .catch(() => live && setStudy([]));
    return () => {
      live = false;
    };
  }, [clientId, kind]);

  const caseTypes = useMemo(
    () =>
      matters.matters
        .filter((m) => isOpenCaseStatus(m.case?.status))
        .map((m) => String(m.case?.visa ?? ""))
        .filter(Boolean),
    [matters.matters]
  );

  return useMemo(() => {
    if (!ready) return { coverage: null, items: [] };
    const now = dayjs();
    const target = coverageTarget(kind, caseTypes, client?.birthday, now);
    const periods = rows.map(periodOf);
    const counted =
      kind === "employment"
        ? [
            ...periods,
            ...study,
            ...periods.filter((p) => EXPLAINED_GAP.test(p.label ?? ""))
          ]
        : periods;
    const coverage = computeCoverage(counted, target, now);
    const dated = rows.map((row, i) => ({
      at: periods[i].current ? now : dayjs(periods[i].start ?? undefined),
      item: {
        type: "row" as const,
        row,
        faded: isBeforeWindow(periods[i], target)
      }
    }));
    const gaps = coverage.gaps.map((gap) => ({
      at: gap.end,
      item: { type: "gap" as const, gap }
    }));
    const items = [...dated, ...gaps]
      .sort((a, b) => b.at.valueOf() - a.at.valueOf())
      .map((x) => x.item as CoverageItem<R>);
    return { coverage, items };
  }, [caseTypes, client?.birthday, kind, periodOf, ready, rows, study]);
};
